import {
  confirmSignUp,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  signIn,
  signOut,
  signUp,
  type SignUpOutput,
} from 'aws-amplify/auth'
import { configureAmplify, isCognitoConfigured } from '@/lib/amplify'
import { clearPersistedAuth, setAccessToken } from '@/lib/api-client'
import { useAuthStore } from '@/store/auth'

export interface SignUpInput {
  email: string
  password: string
  name: string
  phone: string
  referrer?: string
}

export interface SignInInput {
  email: string
  password: string
  rememberMe?: boolean
}

// Session stays in localStorage because Amplify keeps the Cognito session in the browser.
// XSS can read these tokens. Moving them to httpOnly cookies needs a backend session and is deferred.
const LOGOUT_EVENT_KEY = 'esafx_logout'

function persistSession(accessToken: string, rememberMe = true) {
  setAccessToken(accessToken)
  if (rememberMe) {
    localStorage.setItem('accessToken', accessToken)
  }
}

function userFromToken(accessToken: string, fallbackEmail: string) {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]!))
    return {
      userId: (payload.sub as string) || '',
      email: (payload.email as string) || fallbackEmail,
      name: displayNameFromClaims(payload as Record<string, unknown>),
      emailVerified: Boolean(payload.email_verified),
    }
  } catch {
    return {
      userId: '',
      email: fallbackEmail,
      name: '',
      emailVerified: false,
    }
  }
}

export async function registerWithCognito(input: SignUpInput): Promise<SignUpOutput> {
  if (!isCognitoConfigured()) {
    throw new Error('Cognito is not configured')
  }
  configureAmplify()

  const attributes: Record<string, string> = {
    email: input.email,
    name: input.name,
    phone_number: input.phone.startsWith('+') ? input.phone : `+${input.phone.replace(/\D/g, '')}`,
  }
  if (input.referrer?.trim()) {
    attributes['custom:referrer'] = input.referrer.trim()
  }

  return signUp({
    username: input.email,
    password: input.password,
    options: {
      userAttributes: attributes,
    },
  })
}

export async function confirmEmailSignUp(email: string, code: string): Promise<void> {
  configureAmplify()
  await confirmSignUp({ username: email, confirmationCode: code })
}

export async function loginWithCognito(input: SignInInput): Promise<void> {
  if (!isCognitoConfigured()) {
    throw new Error('Cognito is not configured')
  }
  configureAmplify()

  const result = await signIn({
    username: input.email,
    password: input.password,
  })

  if (!result.isSignedIn) {
    throw new Error('Sign in incomplete')
  }

  const session = await fetchAuthSession()
  const access = session.tokens?.accessToken?.toString()
  if (!access) {
    throw new Error('No access token')
  }

  const user = userFromToken(access, input.email)
  persistSession(access, input.rememberMe ?? true)
  useAuthStore.getState().setUser(user)
}

export async function requestPasswordReset(email: string): Promise<void> {
  configureAmplify()
  await resetPassword({ username: email })
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  configureAmplify()
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  })
}

export async function restoreCognitoSession(): Promise<boolean> {
  if (!isCognitoConfigured()) {
    useAuthStore.getState().setLoading(false)
    return false
  }

  configureAmplify()

  try {
    const stored = localStorage.getItem('accessToken')
    if (stored) setAccessToken(stored)

    const session = await fetchAuthSession()
    const access = session.tokens?.accessToken?.toString()
    if (!access) {
      clearPersistedAuth()
      useAuthStore.getState().setUser(null)
      return false
    }

    persistSession(access, true)
    const email = (session.tokens?.idToken?.payload?.email as string) || ''
    const user = userFromToken(access, email)
    useAuthStore.getState().setUser(user)
    return true
  } catch {
    clearPersistedAuth()
    useAuthStore.getState().setUser(null)
    return false
  } finally {
    useAuthStore.getState().setLoading(false)
  }
}

function displayNameFromClaims(payload: Record<string, unknown>): string {
  const email = typeof payload.email === 'string' ? payload.email : ''
  const raw =
    (typeof payload.name === 'string' && payload.name) ||
    (typeof payload['custom:display_name'] === 'string' && payload['custom:display_name']) ||
    ''
  if (!raw.trim() || raw.trim().toLowerCase() === email.trim().toLowerCase()) return ''
  return raw.trim()
}

export function subscribeAuthLogout(onLogout: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === LOGOUT_EVENT_KEY) onLogout()
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}

export async function logoutFromCognito(broadcast = true): Promise<void> {
  if (broadcast) localStorage.setItem(LOGOUT_EVENT_KEY, String(Date.now()))
  clearPersistedAuth()
  if (isCognitoConfigured()) {
    configureAmplify()
    try {
      await signOut({ global: false })
    } catch {
      // ignore
    }
  }
  useAuthStore.getState().logout()
}

/** Map Cognito's English exceptions to i18n keys. Null means show a generic error. */
export function authErrorI18nKey(err: unknown): string | null {
  const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : ''
  const message = err instanceof Error ? err.message : ''
  const lower = message.toLowerCase()
  if (name === 'UsernameExistsException' || lower.includes('user already exists')) return 'auth.userExists'
  if (lower.includes('invalid phone') || (name === 'InvalidParameterException' && lower.includes('phone'))) {
    return 'auth.invalidPhone'
  }
  if (name === 'NotAuthorizedException' || lower.includes('incorrect username or password')) {
    return 'auth.incorrectPassword'
  }
  if (
    name === 'InvalidPasswordException' ||
    lower.includes('password did not conform') ||
    lower.includes('password not long enough')
  ) {
    return 'validation.passwordPolicy'
  }
  return null
}

/** Dev auth from VITE_DEV_* env vars (run-local.ps1). */
export function applyDevAuthFromEnv() {
  const token = import.meta.env.VITE_DEV_AUTH_TOKEN as string | undefined
  const fallbackEmail =
    (import.meta.env.VITE_DEV_USER_EMAIL as string | undefined) || 'client@example.com'

  if (token) {
    persistSession(token, true)
    useAuthStore.getState().setUser(userFromToken(token, fallbackEmail))
  } else {
    console.warn(
      '[auth] VITE_DEV_AUTH_TOKEN is not set. Real API requests will fail with 401 when VITE_USE_MOCKS=false.',
    )
  }

  useAuthStore.getState().setLoading(false)
}

/** Dev/mock auth without Cognito. */
export function applyMockAuth() {
  const mockUser = {
    userId: 'mock-user-1',
    email: 'client@example.com',
    name: 'Budi Santoso',
    emailVerified: true,
  }
  setAccessToken('mock-token')
  localStorage.setItem('accessToken', 'mock-token')
  useAuthStore.getState().setUser(mockUser)
  useAuthStore.getState().setLoading(false)
}
